import { FC, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MenuBook as MenuBookIcon,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import {
  useSyllabusHook,
  useDeleteSyllabusHook,
  useCourseHook,
  useClassHook,
} from "../../../hooks/useAcademicHook";
import {
  TSyllabus,
  TSyllabusStatus,
} from "../../../apis/academic/academic.interfaces";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";
import ManageSyllabusDialog from "./manageSyllabusDialog";
import { hasCoursePrerequisite } from "./academic.helpers";
import { Info as InfoIcon } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";

const SyllabusListView: FC = () => {
  const { selectedInstitute } = useInstituteContext();
  const { data: syllabi, isLoading, error } = useSyllabusHook();
  const { data: courses, isLoading: coursesLoading } = useCourseHook();
  const { data: classes } = useClassHook();
  const deleteSyllabusMutation = useDeleteSyllabusHook();
  const { control, watch } = useForm({
    defaultValues: { search: "", academicYear: "" },
  });
  const searchQuery = watch("search");
  const academicYearFilter = watch("academicYear");
  const [typeFilter, setTypeFilter] = useState<"all" | "course" | "class">(
    "all",
  );
  const [statusFilter, setStatusFilter] = useState<TSyllabusStatus | "all">(
    "all",
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [syllabusToDelete, setSyllabusToDelete] = useState<TSyllabus | null>(
    null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [syllabusToEdit, setSyllabusToEdit] = useState<TSyllabus | null>(null);

  const hasCourses = hasCoursePrerequisite(courses);

  const filteredSyllabi = useMemo(() => {
    if (!syllabi) return [];

    return syllabi.filter((syllabus) => {
      const matchesSearch =
        syllabus.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (syllabus.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ??
          false) ||
        syllabus.academicYear.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = typeFilter === "all" || syllabus.type === typeFilter;
      const matchesStatus =
        statusFilter === "all" || syllabus.status === statusFilter;
      const matchesYear =
        !academicYearFilter || syllabus.academicYear === academicYearFilter;

      return matchesSearch && matchesType && matchesStatus && matchesYear;
    });
  }, [syllabi, searchQuery, typeFilter, statusFilter, academicYearFilter]);

  const getStatusChip = (status: TSyllabusStatus) => {
    const statusConfig = {
      active: {
        label: "Active",
        color: "success" as const,
        icon: <CheckCircle fontSize="small" />,
      },
      inactive: {
        label: "Inactive",
        color: "default" as const,
        icon: <Cancel fontSize="small" />,
      },
    };

    const config = statusConfig[status] || statusConfig.active;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        data-testid={`syllabus-status-${status}`}
      />
    );
  };

  const getAssociatedName = (syllabus: TSyllabus): string => {
    if (syllabus.type === "course" && syllabus.courseId) {
      const course = courses?.find((c) => c.id === syllabus.courseId);
      return course ? course.name : "Unknown Course";
    }
    if (syllabus.type === "class" && syllabus.classId) {
      const classItem = classes?.find((c) => c.id === syllabus.classId);
      return classItem ? classItem.name : "Unknown Class";
    }
    return "Not assigned";
  };

  const handleAddSyllabus = () => {
    setSyllabusToEdit(null);
    setEditDialogOpen(true);
  };

  const handleEditSyllabus = (syllabus: TSyllabus) => {
    setSyllabusToEdit(syllabus);
    setEditDialogOpen(true);
  };

  const handleCloseManageDialog = () => {
    setEditDialogOpen(false);
    setSyllabusToEdit(null);
  };

  const handleDeleteSyllabus = (syllabus: TSyllabus) => {
    setSyllabusToDelete(syllabus);
    setDeleteError(null);

    // Check if syllabus is assigned to a course or class
    if (syllabus.courseId || syllabus.classId) {
      const assignmentType = syllabus.courseId ? "course" : "class";
      const assignmentName = syllabus.courseId
        ? courses?.find((c) => c.id === syllabus.courseId)?.name || "a course"
        : classes?.find((c) => c.id === syllabus.classId)?.name || "a class";
      setDeleteError(
        `Cannot delete syllabus. This syllabus is currently assigned to ${assignmentName}. Please unassign it first from the Syllabus Assignment tab.`,
      );
    }

    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!syllabusToDelete) return;

    if (deleteError) {
      setDeleteDialogOpen(false);
      setSyllabusToDelete(null);
      setDeleteError(null);
      return;
    }

    try {
      await deleteSyllabusMutation.mutateAsync(syllabusToDelete.id);
      logger.show_success("Syllabus deleted successfully!");
      setDeleteDialogOpen(false);
      setSyllabusToDelete(null);
      setDeleteError(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete syllabus. Please try again.";
      // Check if error indicates dependency issue
      if (
        errorMessage.toLowerCase().includes("dependency") ||
        errorMessage.toLowerCase().includes("in use")
      ) {
        setDeleteError(errorMessage);
      } else {
        logger.show_error(errorMessage);
      }
    }
  };

  const uniqueAcademicYears = useMemo(() => {
    if (!syllabi) return [];
    return Array.from(new Set(syllabi.map((s) => s.academicYear))).sort();
  }, [syllabi]);

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Syllabus`} />
        <Box p={3} data-testid="syllabus-list-view">
          <MainTitle title="Syllabus Management" />
          <Alert severity="info" data-testid="syllabus-list-no-institute">
            Please select an institute from the dashboard to view syllabi.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Syllabus`} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="syllabus-list-view"
        >
          <CircularProgress data-testid="syllabus-list-loading" />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Syllabus`} />
        <Box p={3} data-testid="syllabus-list-view">
          <MainTitle title="Syllabus Management" />
          <Alert severity="error" data-testid="syllabus-list-error">
            Failed to load syllabi. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  // Check if courses are still loading
  if (coursesLoading) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Syllabus`} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="syllabus-list-view"
        >
          <CircularProgress data-testid="syllabus-list-loading-courses" />
        </Box>
      </>
    );
  }

  // Check if we should show empty state for course-type syllabi
  // Only show empty state if no courses exist AND no syllabi exist at all
  const showEmptyState = !hasCourses && (!syllabi || syllabi.length === 0);
  const courseTypeSyllabi = syllabi?.filter((s) => s.type === "course") || [];
  const hasCourseTypeSyllabi = courseTypeSyllabi.length > 0;

  return (
    <>
      <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Syllabus`} />

      <Grid container spacing={3} data-testid="syllabus-list-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="syllabus-list-header"
          >
            <MainTitle title="Syllabus Management" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSyllabus}
              size="large"
              data-testid="add-syllabus-button"
            >
              Add Syllabus
            </Button>
          </Box>
        </Grid>

        {showEmptyState ? (
          <Grid item xs={12}>
            <Card data-testid="syllabus-prerequisite-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                  data-testid="syllabus-prerequisite-empty-content"
                >
                  <InfoIcon sx={{ fontSize: 64, color: "info.main" }} />
                  <Typography variant="h6" color="text.secondary">
                    No courses configured
                  </Typography>
                  <Alert
                    severity="info"
                    sx={{ maxWidth: 600, width: "100%" }}
                    data-testid="syllabus-prerequisite-message"
                  >
                    To create course-based syllabi, you must first set up
                    courses in the Courses tab. You can still create class-based
                    syllabi.
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
            {/* Search and Filters */}
            <Grid item xs={12}>
              <Card data-testid="syllabus-filters-card">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <ControlledTextField
                        control={control as any}
                        name="search"
                        id="syllabus-search"
                        label="Search"
                        isGridItem={false}
                        fullWidth
                        placeholder="Search by name, description, or academic year..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ "data-testid": "syllabus-search-input" }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Typography
                          variant="body2"
                          sx={{ alignSelf: "center", minWidth: "50px" }}
                        >
                          Type:
                        </Typography>
                        {(["all", "course", "class"] as const).map((type) => (
                          <Chip
                            key={type}
                            label={
                              type === "all"
                                ? "All"
                                : type.charAt(0).toUpperCase() + type.slice(1)
                            }
                            onClick={() => setTypeFilter(type)}
                            color={typeFilter === type ? "primary" : "default"}
                            variant={
                              typeFilter === type ? "filled" : "outlined"
                            }
                            size="small"
                            data-testid={`syllabus-type-filter-${type}`}
                          />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Typography
                          variant="body2"
                          sx={{ alignSelf: "center", minWidth: "60px" }}
                        >
                          Status:
                        </Typography>
                        {(["all", "active", "inactive"] as const).map(
                          (status) => (
                            <Chip
                              key={status}
                              label={
                                status === "all"
                                  ? "All"
                                  : status.charAt(0).toUpperCase() +
                                  status.slice(1)
                              }
                              onClick={() => setStatusFilter(status)}
                              color={
                                statusFilter === status ? "primary" : "default"
                              }
                              variant={
                                statusFilter === status ? "filled" : "outlined"
                              }
                              size="small"
                              data-testid={`syllabus-status-filter-${status}`}
                            />
                          ),
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ControlledSelectField
                        control={control as any}
                        name="academicYear"
                        id="syllabus-academic-year-filter"
                        label="Academic Year"
                        isGridItem={false}
                        itemList={[
                          { key: "", value: "All Academic Years" },
                          ...uniqueAcademicYears.map((year) => ({
                            key: year,
                            value: year,
                          })),
                        ]}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Syllabus List */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom data-testid="syllabi-count">
                Syllabi ({filteredSyllabi.length})
              </Typography>
            </Grid>

            {filteredSyllabi.length === 0 ? (
              <Grid item xs={12}>
                <Card data-testid="syllabus-empty-state">
                  <CardContent>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="200px"
                      gap={2}
                      data-testid="syllabus-empty-content"
                    >
                      <MenuBookIcon
                        sx={{ fontSize: 64, color: "text.secondary" }}
                      />
                      <Typography variant="h6" color="text.secondary">
                        {syllabi?.length === 0
                          ? "No syllabi configured"
                          : "No syllabi match your search criteria"}
                      </Typography>
                      {syllabi?.length === 0 && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Get started by adding your first syllabus.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddSyllabus}
                            data-testid="add-syllabus-empty-button"
                          >
                            Add Syllabus
                          </Button>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  data-testid="syllabus-table-container"
                >
                  <Table data-testid="syllabus-table">
                    <TableHead data-testid="syllabus-table-head">
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Associated Course/Class</TableCell>
                        <TableCell>Academic Year</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-testid="syllabus-table-body">
                      {filteredSyllabi.map((syllabus) => (
                        <TableRow
                          key={syllabus.id}
                          hover
                          data-testid={`syllabus-row-${syllabus.id}`}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {syllabus.name}
                              </Typography>
                              {syllabus.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {syllabus.description}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                syllabus.type === "course" ? "Course" : "Class"
                              }
                              variant="outlined"
                              size="small"
                              data-testid={`syllabus-type-${syllabus.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {getAssociatedName(syllabus)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {syllabus.academicYear}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {getStatusChip(syllabus.status)}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleEditSyllabus(syllabus)}
                              color="primary"
                              disabled={syllabus.status === "inactive"}
                              data-testid={`edit-syllabus-${syllabus.id}`}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSyllabus(syllabus)}
                              color="error"
                              data-testid={`delete-syllabus-${syllabus.id}`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSyllabusToDelete(null);
          setDeleteError(null);
        }}
        data-testid="delete-syllabus-dialog"
      >
        <DialogTitle data-testid="delete-syllabus-dialog-title">
          Delete Syllabus
        </DialogTitle>
        <DialogContent data-testid="delete-syllabus-dialog-content">
          {deleteError ? (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="delete-syllabus-error"
            >
              {deleteError}
            </Alert>
          ) : null}
          <DialogContentText>
            Are you sure you want to delete syllabus "{syllabusToDelete?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions data-testid="delete-syllabus-dialog-actions">
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSyllabusToDelete(null);
            }}
            disabled={deleteSyllabusMutation.isPending}
            data-testid="cancel-delete-syllabus-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteSyllabusMutation.isPending || !!deleteError}
            startIcon={
              deleteSyllabusMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-syllabus-button"
          >
            {deleteSyllabusMutation.isPending
              ? "Deleting..."
              : deleteError
                ? "Close"
                : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ManageSyllabusDialog
        open={editDialogOpen}
        onClose={handleCloseManageDialog}
        syllabus={syllabusToEdit}
      />
    </>
  );
};

export default SyllabusListView;
